import { projects } from "@folio-2026/content"
import Experience from "@folio-2026/experience"
import { AnimatedText, EVENTS, Grid, SmallButton } from "@folio-2026/shared"
import { gsap } from "gsap/all"
import Lenis from "lenis"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { useParams } from "react-router"

import useSectionReady from "../../../hooks/useSectionReady"

export default function ProjectContent() {
  const isSectionReady = useSectionReady("Works")

  const { project: projectSlug } = useParams()

  const projectData = useMemo(() => {
    if (!projectSlug) return null

    const data = projects.find((p) => p.url.replace("/", "") === projectSlug)
    if (!data) {
      throw new Error(`Project data not found: [${projectSlug}]`)
    }
    return data
  }, [projectSlug])

  const content = useMemo(() => {
    const contentMap = new Map()
    projectData?.content.forEach((item) => {
      Object.entries(item).forEach(([key, value]) => {
        contentMap.set(key, value)
      })
    })
    return contentMap
  }, [projectData])

  // const awardsPool = useMemo(() => {
  //   return getAwardsPool({ projectData })
  // }, [projectData])

  const triggerRef = useRef<HTMLDivElement | null>(null)
  const targetRef = useRef<HTMLDivElement | null>(null)

  const lenisProjectRef = useRef<Lenis | null>(null)
  const lenisWrapperRef = useRef<HTMLDivElement | null>(null)
  const lenisContentRef = useRef<HTMLDivElement | null>(null)
  const gsapTickerRef = useRef<((time: number) => void) | null>(null)
  const hasRegisteredLenisInitListenerRef = useRef(false)
  const previousProjectSlugRef = useRef<string | undefined>(undefined)
  const previousProjectDataRef = useRef<(typeof projects)[number] | null>(null)

  const initProjectLenis = useCallback(() => {
    const experience = Experience.getInstance()

    // Scroll to the project before stopping Lenis
    const projectElement = document.querySelector(
      `[data-project="${projectSlug}"]`,
    ) as HTMLElement
    if (projectElement) {
      experience.lenis?.scrollTo(projectElement, {
        immediate: true,
      })
    } else {
      console.warn(`Project element not found: [data-project="${projectSlug}"]`)
    }

    lenisProjectRef.current = new Lenis({
      wrapper: lenisWrapperRef.current ?? undefined,
      content: lenisContentRef.current ?? undefined,
    })

    lenisProjectRef.current.on("scroll", () => {
      experience.trigger(EVENTS.Project.Scroll, [
        {
          animatedScroll: lenisProjectRef.current?.animatedScroll ?? 0,
          actualScroll: lenisProjectRef.current?.actualScroll ?? 0,
        },
      ])
    })

    // Add Lenis's requestAnimationFrame (raf) method to GSAP's ticker
    if (gsapTickerRef.current) {
      gsap.ticker.remove(gsapTickerRef.current)
      gsapTickerRef.current = null
    }

    const tickerHandler = (time: number) => {
      lenisProjectRef.current?.raf(time * 1000)
    }
    gsapTickerRef.current = tickerHandler
    gsap.ticker.add(tickerHandler)

    // Disable lag smoothing in GSAP to prevent any delay in scroll animations
    gsap.ticker.lagSmoothing(0)
  }, [projectSlug])

  const destroyProjectLenis = () => {
    if (gsapTickerRef.current) {
      gsap.ticker.remove(gsapTickerRef.current)
      gsapTickerRef.current = null
    }

    lenisProjectRef.current?.destroy()
    lenisProjectRef.current = null
  }

  const closeProjectClickHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // ? ScrollTrigger requires avoiding pointer-events-none classes
      // ? The canvas isn't reachable for this button

      const experience = Experience.getInstance()

      experience.trigger(EVENTS.Project.CloseCTA.Click, [
        {
          target: e.currentTarget,
          htmlElement: e.currentTarget,
        },
      ])
    },
    [],
  )

  useEffect(() => {
    if (!isSectionReady) {
      return
    }

    const experience = Experience.getInstance()
    const currentSlug = projectSlug
    const previousSlug = previousProjectSlugRef.current
    const previousData = previousProjectDataRef.current

    if (previousSlug === currentSlug) return

    const exitPrevious = () => {
      if (!previousData) return

      destroyProjectLenis()
      experience.trigger(EVENTS.Project.Exiting, [previousData])
    }

    const enterCurrent = () => {
      if (!projectData) return

      const enter = () => {
        initProjectLenis()
        experience.trigger(EVENTS.Project.Entered, [
          {
            ...projectData,
            target: targetRef.current,
            trigger: triggerRef.current,
          },
        ])
      }

      if (experience.lenis) {
        enter()
        return
      }

      if (hasRegisteredLenisInitListenerRef.current) return
      hasRegisteredLenisInitListenerRef.current = true
      experience.on(EVENTS.Lenis.Initialized, () => {
        hasRegisteredLenisInitListenerRef.current = false
        enter()
      })
    }

    // project -> /
    if (previousSlug && !currentSlug) {
      exitPrevious()
      experience.lenis?.start()

      setTimeout(() => {
        experience.lenis?.resize()
      }, 500)

      previousProjectSlugRef.current = currentSlug
      previousProjectDataRef.current = null
      return
    }

    // / -> project
    if (!previousSlug && currentSlug) {
      enterCurrent()
      previousProjectSlugRef.current = currentSlug
      previousProjectDataRef.current = projectData
      return
    }

    // project A -> project B
    if (previousSlug && currentSlug) {
      exitPrevious()
      enterCurrent()
      previousProjectSlugRef.current = currentSlug
      previousProjectDataRef.current = projectData
    }
  }, [isSectionReady, initProjectLenis, projectData, projectSlug])

  const isOpen = Boolean(projectSlug && projectData)

  return (
    <div
      data-name="project"
      className={[
        "fixed w-screen h-screen z-10 top-0 left-0",
        isOpen
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none",
      ].join(" ")}
    >
      <SmallButton
        preventUpdate
        id="close-project-cta"
        className="absolute top-4 z-11 left-4 w-15 text-gray-300 lg:hidden"
        onClick={closeProjectClickHandler}
        hoverEvent={EVENTS.Project.CloseCTA.Hover}
        outEvent={EVENTS.Project.CloseCTA.Out}
        data-project-field="close-cta"
      >
        Close
      </SmallButton>
      <div
        ref={lenisWrapperRef}
        className="h-screen overflow-x-hidden text-white"
      >
        <div ref={lenisContentRef}>
          <div ref={triggerRef} data-trigger className="h-screen">
            <div ref={targetRef} data-target className="h-full">
              <Grid className="h-full">
                <div className="col-span-4 md:col-span-6 lg:col-span-5 md:col-start-2 lg:col-start-7 py-[20vh] md:py-[15vh] h-full relative md:pl-3">
                  <div className="flex flex-col gap-10 justify-between h-full">
                    <div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="col-span-1">
                          <AnimatedText
                            preventUpdate
                            data-font-bold
                            content="Client_"
                            tag="h1"
                            className="text-gray-500"
                            data-project-field="label-client"
                          />

                          <AnimatedText
                            preventUpdate
                            data-font-bold
                            content={content.get("client") ?? ""}
                            data-project-field="client"
                          />
                        </div>
                        <div className="col-span-1">
                          <AnimatedText
                            preventUpdate
                            data-font-bold
                            content="Year_"
                            tag="h1"
                            className="text-gray-500"
                            data-project-field="label-year"
                          />

                          <AnimatedText
                            preventUpdate
                            data-font-bold
                            content={content.get("year") ?? ""}
                            data-project-field="year"
                          />
                        </div>
                      </div>

                      <AnimatedText
                        className="mt-4 text-gray-500"
                        preventUpdate
                        data-font-bold
                        content="Role_"
                        tag="h1"
                        data-project-field="label-role"
                      />

                      <AnimatedText
                        preventUpdate
                        data-font-bold
                        content={content.get("role") ?? ""}
                        data-project-field="role"
                      />

                      <SmallButton
                        preventUpdate
                        id="visit-website-cta"
                        className={[
                          "mt-8 text-gray-300 w-25",
                          projectData?.website
                            ? ""
                            : "opacity-0 pointer-events-none select-none",
                        ].join(" ")}
                        hoverEvent={EVENTS.Project.VisitWebsiteCTA.Hover}
                        outEvent={EVENTS.Project.VisitWebsiteCTA.Out}
                        onMouseEnter={() => {
                          const experience = Experience.getInstance()
                          experience.trigger(
                            EVENTS.Project.VisitWebsiteCTA.Hover,
                          )
                        }}
                        onMouseLeave={() => {
                          const experience = Experience.getInstance()
                          experience.trigger(EVENTS.Project.VisitWebsiteCTA.Out)
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (!projectData?.website) return
                          window.open(projectData.website, "_blank")
                        }}
                        data-project-field="visit-website-cta"
                      >
                        Visit Project
                      </SmallButton>
                    </div>
                    <div className="max-w-3/4 lg:max-w-2/3">
                      <AnimatedText
                        preventUpdate
                        data-font-bold
                        content={content.get("description") ?? ""}
                        tag="p"
                        data-project-field="description"
                      />
                    </div>
                  </div>
                </div>
              </Grid>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// const awardsPoolSize = getAwardsPoolSize()

// function getAwardsPoolSize() {
//   let maxCount = 0

//   for (const project of projects) {
//     const awards = project.content.find((item) => item.awards)?.awards
//     if (!Array.isArray(awards)) continue
//     maxCount = Math.max(maxCount, awards.length)
//   }

//   // Always render at least one row so DOM shape is stable.
//   return Math.max(1, maxCount)
// }

// function getAwardsPool({
//   projectData,
// }: {
//   projectData: (typeof projects)[number] | null
// }) {
//   const awards = projectData?.content.find((item) => item.awards)?.awards
//   const normalized = Array.isArray(awards) ? awards : []

//   const pool: string[] = []
//   for (let i = 0; i < awardsPoolSize; i++) {
//     pool.push(normalized[i] ?? "")
//   }

//   return pool
// }
